import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useStytchUser } from "../utils/useStytch";
import { hasDevArticleBookmarkFixture } from "./dev/articleBookmarkFixture";
import { isDevCareTeamReview } from "./dev/devCareTeamReview";

type AuthGateContextValue = {
  showAuthGate: boolean;
};

const AuthGateContext = createContext<AuthGateContextValue>({
  showAuthGate: true,
});

function shouldBypassAuthGateForDevReview() {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_patient_medication_actions") === "true" ||
      params.get("dev_clinic_detail") === "us" ||
      hasDevArticleBookmarkFixture()
    );
  } catch {
    return false;
  }
}

export function AuthGateProvider({ children }: { children: ReactNode }) {
  const { user } = useStytchUser();
  const bypassAuthGateForDevReview = useMemo(
    () => shouldBypassAuthGateForDevReview() || isDevCareTeamReview(),
    [],
  );

  const showAuthGate = !bypassAuthGateForDevReview && !user;

  // Memoize so the context value keeps a stable identity across renders.
  const value = useMemo(() => ({ showAuthGate }), [showAuthGate]);

  return (
    <AuthGateContext.Provider value={value}>
      {children}
    </AuthGateContext.Provider>
  );
}

export function useAuthGate() {
  return useContext(AuthGateContext);
}
